import { groupBy, isEmpty } from 'lodash';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { safeLocalStorage } from 'helpers/localStorage';
import { NavigationEntity, useSidebarNavigationEntities } from 'hooks/useNavigationEntities';
import usePreviousValue from 'hooks/usePreviousValue';

type ToggleState = { [id: string]: boolean };
type SetToggleState = (id: string, isActive?: boolean) => void;

const ToggleStateContext = createContext<{ state: ToggleState; setState: SetToggleState }>({
  state: {},
  setState: () => {},
});

export const ToggleStateProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { all: entities } = useSidebarNavigationEntities();
  const [toggleState, setToggleState] = useGlobalToggleState(entities);
  const ctx = useMemo(
    () => ({ state: toggleState, setState: setToggleState }),
    [toggleState, setToggleState],
  );

  return <ToggleStateContext.Provider value={ctx}>{children}</ToggleStateContext.Provider>;
};

export function useEntityToggleState(
  entity: NavigationEntity,
): [boolean, (isActive?: boolean) => void] {
  const { state, setState } = useContext(ToggleStateContext);

  return [state[entity.id] ?? true, (value?: boolean) => setState(entity.id, value)];
}

function useGlobalToggleState(entities: NavigationEntity[]): [ToggleState, (id: string) => void] {
  const computedState = useMemo(() => computeToggleState(entities), [entities]);
  const localState = useMemo(() => loadLocalState(computedState), [computedState]);
  const isEmptyLocalState = isEmpty(localState);
  const wasEmptyLocalState = usePreviousValue(isEmptyLocalState);

  const [toggleState, rawSetToggleState] = useState<ToggleState>(localState);
  useEffect(() => {
    // load the default local state if it was empty before
    if (wasEmptyLocalState && !isEmptyLocalState) {
      rawSetToggleState(localState);
    }
  }, [isEmptyLocalState, wasEmptyLocalState, localState]);

  const setToggleState = useCallback(
    (id: string, isActive?: boolean) => {
      rawSetToggleState((currentToggleState) => {
        if (typeof isActive === 'undefined') {
          isActive = !currentToggleState[id];
        }
        const newState = { ...currentToggleState, [id]: isActive };
        saveState(newState);
        return newState;
      });
    },
    [rawSetToggleState],
  );

  return [toggleState, setToggleState];
}

function computeToggleState(entities: NavigationEntity[]) {
  const byToggle = groupBy(entities, (entity) => (entity.children.length !== 0 ? 'on' : 'off'));

  const shouldBeOn = byToggle.on ?? [];
  return shouldBeOn.reduce((accumulator, entity) => {
    accumulator[entity.id] = true;
    return accumulator;
  }, {} as ToggleState);
}

const LOCAL_STORAGE_KEY = 'navigation.toggleState';
function loadLocalState(computedState: ToggleState) {
  const rawLocalState = safeLocalStorage.getItem(LOCAL_STORAGE_KEY);
  if (rawLocalState == null) {
    if (!isEmpty(computedState)) {
      saveState(computedState);
    }
    return computedState;
  }

  const localState = JSON.parse(rawLocalState) as ToggleState;
  let isModifed = false;
  for (const [id, toggle] of Object.entries(computedState)) {
    if (id in localState) {
      continue;
    }
    localState[id] = toggle;
    isModifed = true;
  }

  if (isModifed) {
    saveState(localState);
  }

  return localState;
}

function saveState(state: ToggleState) {
  safeLocalStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
}
